import { useParams } from "react-router-dom";

/**
 * The single source of truth for the tenant-scoped link base in the UI-v2 tree.
 *
 * Returns `/${tenantSlug}` — the prefix every in-app v2 link and `navigate()`
 * target is built from. Call it in place of hand-rolling `` `/${tenantSlug}/...` ``
 * so the base lives in exactly one place.
 *
 * WHY THIS EXISTS — v2 was built under a `/v2` URL segment while v1 owned the
 * tenant root. This hook was introduced (PR2) as a pure indirection returning
 * `` `/${slug}/v2` ``, so ~40 call sites across 21 files could adopt it while
 * routing byte-for-byte identically to the literals they replaced — the visual
 * suite passed unchanged (it fails on any pixel diff), confirming no behavior change.
 *
 * That existed for exactly one purpose: THE FLIP (PR4). v2 is now the default
 * tenant UX served at the root, and dropping the `/v2` segment for all ~40 sites
 * was the one-line return change below — reviewable and revertible — instead of a
 * 21-file find-and-replace done under pressure.
 * v1 lives on, parked at `/:tenantSlug/v1/*`.
 *
 * Do not re-inline the base at call sites; that re-creates the problem this hook
 * was built to solve, and there are more URL changes to come.
 *
 * SCOPE — only valid inside a component rendered under a route with a
 * `:tenantSlug` param (the `/:tenantSlug` subtree and its slug-scoped auth
 * siblings). It reads the param from the router, so it is NOT usable where the
 * slug comes from somewhere else:
 *   - the slug-less public routes (`/login`, `/verify`), which derive a slug from
 *     localStorage / user input / an API response, and
 *   - `router.tsx`'s own route table, which *declares* where the tree lives
 *     rather than linking into it.
 * Those sites keep building their paths by hand — see the PR notes.
 *
 * The `?? ""` guard is unreachable in practice: every caller sits under a route
 * that binds the param, so the slug is always defined. It is kept (and pinned by
 * a unit test) so the fallback can never silently drift into emitting the string
 * "undefined" inside a URL — `/undefined/dashboard` would be a real, valid-looking
 * path that 404s at RequireValidSlug, whereas `/` fails loudly and harmlessly.
 */
export function useTenantBase(): string {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  return `/${tenantSlug ?? ""}`;
}
